/**
 * Where the TV app reads its own query string from.
 *
 * WHY THIS IS NOT JUST `window.location.search`.
 *
 * On Samsung Tizen the app is not navigated to - it is TRANSCLUDED. The WGT
 * bootloader (`tizen/index.html`) fetches the app's HTML and re-creates its
 * scripts inside the WIDGET's own document, because navigating to a remote
 * origin makes Tizen stop injecting `window.tizen` and every device capability
 * with it (measured on hardware, 2026-08-10 - see the `tizen-port` memory note
 * and TIZEN-4).
 *
 * The consequence: the document's URL is the widget's own
 * `file:///opt/usr/apps/.../index.html`, with no query. `?probe=1` in the URL
 * the bootloader FETCHED only decided which HTML the server returned; it never
 * reaches `location.search`. And the obvious repair does not work either -
 * `history.replaceState(null, "", "?probe=1")` is silently a no-op on an opaque
 * file-ish origin (verified in a browser before it reached a panel).
 *
 * So a host that cannot express a query says so explicitly instead:
 *
 *     window.__FOYER_SEARCH__ = "?probe=1";
 *
 * set before the app's scripts are injected. A real browser sets nothing and
 * `location.search` wins, so this costs the Android and web lanes nothing.
 *
 * `location.search` is checked FIRST on purpose: on a platform where the URL
 * does work, the URL is the truth, and a stale global left behind by a host
 * must never override what the operator actually typed.
 */

declare global {
  interface Window {
    /** Set by a host that cannot put a query on the document (the Tizen WGT). */
    __FOYER_SEARCH__?: string;
  }
}

/**
 * The effective query string, always leading with `?` (or empty).
 * Never throws: a broken host value degrades to "no query", which lands the
 * app on its normal boot path rather than a blank screen.
 */
export function hostSearch(): string {
  try {
    const fromUrl = window.location.search;
    if (fromUrl && fromUrl !== "?") return fromUrl;

    const injected = window.__FOYER_SEARCH__;
    if (typeof injected !== "string" || !injected) return "";
    return injected.charAt(0) === "?" ? injected : "?" + injected;
  } catch {
    return "";
  }
}

/** The effective query, parsed. */
export function hostParams(): URLSearchParams {
  return new URLSearchParams(hostSearch());
}
